import React, { useState, useRef, useEffect } from 'react';
import { GoogleGenerativeAI } from "@google/generative-ai";

// ❌ CORRECTION CRITIQUE : On NE met plus la clé ici.
// ✅ On utilise la variable d'environnement sécurisée.
const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_KEY; 

// 🧠 CONFIGURATION IA
const SYSTEM_PROMPT = `
RÔLE : Tu es l'IA officielle de "District Zone 25" (NDI 2025).
TON : Cyberpunk, bref, efficace, futuriste.

DONNÉES OFFICIELLES :
- Chef de projet / Lead : Rockssan Hounton.
- Fullstack : Junior Useni.
- UI/UX : Yacine Maacha.
- Frontend : Aiman Gamine.
- Stack : React, Supabase, Gemini AI.

RÈGLES :
1. Si on demande "Qui est le chef ?", réponds Rockssan Hounton.
2. Ne réponds qu'aux questions sur l'équipe ou le projet.
3. Si c'est hors-sujet, refuse poliment avec une phrase type "Accès refusé : Hors périmètre District 25".
`;

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  
  const [messages, setMessages] = useState([
    { sender: 'bot', text: "Salut ! Que puis-je faire pour vous ?" }
  ]);
  
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isOpen]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    // Vérification de sécurité avant d'appeler l'API
    if (!GEMINI_API_KEY) {
        setMessages(prev => [...prev, { sender: 'bot', text: "⚠️ ERREUR : Clé API non configurée. Vérifiez Vercel/ficher .env." }]);
        return;
    }

    const userMsg = { sender: 'user', text: input };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      // NOTE : Si le modèle pose problème à nouveau, essayez "gemini-pro-latest"
      const genAI = new GoogleGenerativeAI(GEMINI_API_KEY);
      const model = genAI.getGenerativeModel({ model: "gemini-flash-latest" }); 

      const fullPrompt = `${SYSTEM_PROMPT}\n\nQuestion utilisateur : ${userMsg.text}`;
      const result = await model.generateContent(fullPrompt);
      const response = await result.response;
      const text = response.text();

      setMessages(prev => [...prev, { sender: 'bot', text: text }]);
    } catch (error) {
      console.error("Erreur:", error);
      setMessages(prev => [...prev, { sender: 'bot', text: "Erreur de transmission (Réseau)." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      
      {/* Chargement de la police d'écriture */}
      <style>
        {`@import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&display=swap');`}
      </style>

      {/* FENÊTRE DE CHAT */}
      {isOpen && (
        <div style={styles.chatWindow}>
          
          <div style={styles.header}>
            <span style={styles.headerTitle}>Votre Assistant</span>
            <button onClick={() => setIsOpen(false)} style={styles.closeBtn}>×</button>
          </div>

          <div style={styles.box}>
            {messages.map((m, i) => (
              <div key={i} style={{...styles.msg, ...(m.sender === 'user' ? styles.user : styles.bot)}}>
                {m.text}
              </div>
            ))}
            {loading && <div style={styles.loading}>Analyse en cours...</div>}
            <div ref={messagesEndRef} />
          </div>

          <div style={styles.inputZone}>
            <input 
              value={input} 
              onChange={e => setInput(e.target.value)} 
              onKeyPress={e => e.key === 'Enter' && sendMessage()}
              placeholder="Message..."
              style={styles.input}
              autoFocus
            />
            <button onClick={sendMessage} style={styles.sendBtn}>➤</button>
          </div>
        </div>
      )}

      {/* BOUTON FLOTTANT */}
      <button 
        onClick={() => setIsOpen(!isOpen)} 
        style={styles.floatingBtn}
      >
        {isOpen ? '▼' : '🤖'}
      </button>

    </div>
  );
};

// --- STYLES (Identiques) ---
const styles = {
  chatWindow: {
    width: '320px',
    height: '450px',
    marginBottom: '15px',
    background: 'rgba(9, 14, 28, 0.95)',
    border: '1px solid #00f0ff',
    borderRadius: '12px',
    boxShadow: '0 0 20px rgba(0, 240, 255, 0.4)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backdropFilter: 'blur(5px)',
    animation: 'fadeIn 0.3s ease-in-out'
  },
  header: {
    background: 'linear-gradient(90deg, #00f0ff 0%, #00aaff 100%)',
    color: '#000',
    padding: '10px 15px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerTitle: {
    fontFamily: "'Dancing Script', cursive",
    fontSize: '1.8rem',
    fontWeight: 'bold',
    letterSpacing: '1px',
    textShadow: '0px 0px 2px rgba(255,255,255,0.5)'
  },
  closeBtn: {
    background: 'transparent',
    border: 'none',
    fontSize: '24px',
    fontWeight: 'bold',
    cursor: 'pointer',
    color: '#000',
    lineHeight: '1',
    marginLeft: '10px'
  },
  box: {
    flex: 1,
    overflowY: 'auto',
    padding: '15px',
    color: '#fff',
    fontFamily: 'Roboto, sans-serif',
    fontSize: '0.9rem'
  },
  msg: {
    padding: '10px 14px',
    margin: '8px 0',
    borderRadius: '8px',
    lineHeight: '1.4',
    maxWidth: '85%',
    wordWrap: 'break-word'
  },
  user: {
    background: 'linear-gradient(135deg, #00f0ff 0%, #005f73 100%)',
    color: '#000',
    alignSelf: 'flex-end',
    marginLeft: 'auto',
    borderBottomRightRadius: '0',
    boxShadow: '0 2px 5px rgba(0,0,0,0.3)'
  },
  bot: {
    background: '#1a1a1a',
    borderLeft: '3px solid #00f0ff',
    color: '#e0e0e0',
    marginRight: 'auto',
    borderTopLeftRadius: '0'
  },
  loading: {
    color: '#00f0ff',
    fontSize: '0.8rem',
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: '5px'
  },
  inputZone: {
    display: 'flex',
    borderTop: '1px solid rgba(0, 240, 255, 0.3)',
    padding: '10px',
    background: 'rgba(0,0,0,0.3)'
  },
  input: {
    flex: 1,
    background: 'transparent',
    border: 'none',
    color: 'white',
    padding: '8px',
    outline: 'none',
    fontSize: '1rem'
  },
  sendBtn: {
    background: 'transparent',
    border: 'none',
    color: '#00f0ff',
    fontSize: '1.2rem',
    cursor: 'pointer',
    padding: '0 10px',
    transition: 'transform 0.2s'
  },
  floatingBtn: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    background: '#00f0ff',
    color: '#000',
    border: 'none',
    fontSize: '30px',
    cursor: 'pointer',
    boxShadow: '0 0 15px #00f0ff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
    zIndex: 10000
  }
};

export default Chatbot;